import React, { useMemo, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ZoningSection } from '../../../ZoningSection'
import { useAnalysisMode, useAnalysisServices } from '../../contexts'
import { useAnalysisMapSectionData } from '../../contexts/MapSectionDataContext'
import type { Zoning, ZoningType } from '@popety-io/types'

export const ZoningTab: React.FC = () => {
  const { t, i18n } = useTranslation('zoning')
  const { entity, readOnly } = useAnalysisMode()
  const { getSectionData } = useAnalysisServices()
  const { setActiveSection, updateSectionData: updateMapData } =
    useAnalysisMapSectionData()

  // Set active map section
  useEffect(() => {
    setActiveSection('zoning_data')
    return () => {
      setActiveSection(null)
    }
  }, [setActiveSection])

  // Extract zoning data (readOnly only — pre-fetched from entity)
  const zoningData = readOnly ? (entity?.zoning as any) : null

  // Push zoning data to map when available (readOnly)
  useEffect(() => {
    if (readOnly && zoningData?.zones) {
      updateMapData({
        zoningData: {
          general: zoningData.zones.lupa || [],
          cantonal: zoningData.zones.lupc || [],
          local: zoningData.zones.lupo || [],
          partial: zoningData.zones.lup || [],
        },
      })
    }
  }, [readOnly, zoningData?.zones, updateMapData])

  // Callback to receive zoning data from ZoningSection (interactive mode)
  const handleZoningDataChange = useCallback(
    (data: any) => {
      updateMapData({ zoningData: data })
    },
    [updateMapData]
  )

  // Land object for ZoningSection
  const land = useMemo(() => {
    if (!readOnly) return entity

    // ReadOnly: create mock land with zone references from stored zones
    const createReferences = (zones: Zoning[] | undefined) =>
      (zones || []).map((zone) => ({ id: zone._id || '' }))

    return {
      id: entity?.land_id || '',
      land_use_plans_general: createReferences(zoningData?.zones?.lupa),
      land_use_plans_cantonal: createReferences(zoningData?.zones?.lupc),
      land_use_plans_local: createReferences(zoningData?.zones?.lupo),
      land_use_plans_partial: createReferences(zoningData?.zones?.lup),
    }
  }, [readOnly, entity, zoningData?.zones])

  // getZonesByIds: adapter for readOnly, injected service for interactive
  const getZonesByIds = useCallback(
    async (type: ZoningType, ids: string[]): Promise<Zoning[]> => {
      if (!readOnly) {
        const service = getSectionData('getZonesByIds')
        return service?.(type, ids) || []
      }

      // ReadOnly: return stored zones filtered by IDs
      const typeToKey: Record<string, string> = {
        general: 'lupa',
        cantonal: 'lupc',
        local: 'lupo',
        partial: 'lup',
        lupa: 'lupa',
        lupc: 'lupc',
        lupo: 'lupo',
        lup: 'lup',
      }
      const storedZones = zoningData?.zones?.[typeToKey[type]] || []
      return storedZones.filter((zone: Zoning) =>
        ids.includes(zone._id || (zone as any).id || '')
      )
    },
    [readOnly, getSectionData, zoningData?.zones]
  )

  // Geoportal link (from Land entity, resolved via bridge with proj4)
  const geoPortalLink = readOnly ? undefined : getSectionData('geoPortalLink')

  // Land data for metrics
  const landData = useMemo(() => {
    if (readOnly) return zoningData?.landData || {}
    // Interactive mode: extract from entity
    return {
      std: entity?.std ?? null,
      sbd: entity?.sbd ?? null,
      sb: entity?.sb ?? null,
      spd: entity?.spd ?? null,
      sbpu: entity?.sbpu ?? null,
      sp: entity?.sp ?? null,
      vbr: entity?.vbr ?? null,
      ios: entity?.ios ?? null,
      cos: entity?.cos ?? null,
      ius: entity?.ius ?? null,
      cus: entity?.cus ?? null,
      ibus: entity?.ibus ?? null,
      mass_index: entity?.im ?? null,
      max_height: entity?.max_height ?? null,
      max_floor_nb: entity?.max_floor_nb ?? null,
      unit_nb: entity?.unit_nb ?? null,
      centerChProjection: entity?.center_ch_projection ?? undefined,
      geoCenter: entity?.geo_center ?? undefined,
      geoPortalLink: geoPortalLink || undefined,
      landGeoPolygon: entity?.geo_polygon ?? undefined,
      // Regelbauweise envelope score (null on classic-indice lands)
      indice_used: entity?.indice_used ?? null,
      volume_envelope_authorized: entity?.volume_envelope_authorized ?? null,
      volume_envelope_difference: entity?.volume_envelope_difference ?? null,
      buildable_area_setback: entity?.buildable_area_setback ?? null,
      buildable_area_effective: entity?.buildable_area_effective ?? null,
      setback_differentiated: entity?.setback_differentiated ?? null,
      building_length_capped: entity?.building_length_capped ?? null,
      building_depth_capped: entity?.building_depth_capped ?? null,
      envelope_height_used: entity?.envelope_height_used ?? null,
    }
  }, [readOnly, zoningData?.landData, entity, geoPortalLink])

  // Services (interactive mode only, injected via bridge)
  const zoningOverridesService = readOnly
    ? undefined
    : getSectionData('zoningOverridesService')
  const zoningService = getSectionData('zoningService') || undefined
  const onFetchEstimateStats = readOnly
    ? undefined
    : getSectionData('onFetchEstimateStats')
  const zoneQAService = readOnly ? undefined : getSectionData('zoneQAService')
  const onTranscribeVoice = readOnly
    ? undefined
    : getSectionData('onTranscribeVoice')
  const onReportIncorrect = readOnly
    ? undefined
    : getSectionData('onReportIncorrect')
  // Zone Q&A translations (built from same i18n namespace)
  const zoneQATranslations = useMemo(
    () =>
      readOnly
        ? undefined
        : {
            title: t('zoneQA.title'),
            placeholder: t('zoneQA.placeholder'),
            send: t('zoneQA.send'),
            loading: t('zoneQA.loading'),
            emptyState: t('zoneQA.emptyState'),
            suggestDocs: t('zoneQA.suggestDocs'),
            errorRetry: t('zoneQA.errorRetry'),
            citationLabels: {
              communal: t('zoneQA.citationLabels.communal'),
              cantonal: t('zoneQA.citationLabels.cantonal'),
            },
          },
    [readOnly, t]
  )

  // Under-exploited score
  const underExploitedScore = readOnly
    ? zoningData?.underExploitedScore
    : entity?.under_exploited_score

  // Land ID
  const landId = readOnly ? entity?.land_id || '' : entity?.id || ''

  // Loading/error (interactive mode)
  const loading = readOnly ? false : getSectionData('loading') || false
  const error = readOnly ? null : getSectionData('error') || null

  // Build translations object
  const translations = useMemo(
    () => ({
      title: t('title'),
      sectionDescription: readOnly
        ? t('subtitle')
        : t('subtitle', { address: entity?.address_full || '' }),
      underExploited: t('underExploitedScore'),
      loading: t('messages.loading'),
      error: t('messages.error'),
      noData: t('messages.noData'),
      noZonesForType: t('messages.noZonesForType'),
      unknownZone: t('unknownZone'),
      zoneTypes: {
        local: t('zoneTypes.local'),
        cantonal: t('zoneTypes.cantonal'),
        partial: t('zoneTypes.partial'),
        general: t('zoneTypes.general'),
      },
      zonePriorityLabels: {
        local: t('zonePriorityLabels.local'),
        cantonal: t('zonePriorityLabels.cantonal'),
        partial: t('zonePriorityLabels.partial'),
        general: t('zonePriorityLabels.general'),
      },
      zoneDescriptions: {
        local: t('zoneDescriptions.local'),
        cantonal: t('zoneDescriptions.cantonal'),
        partial: t('zoneDescriptions.partial'),
        general: t('zoneDescriptions.general'),
      },
      metricsTable: {
        title: t('metricsTitle'),
        resetToDefault: t('metricsTable.resetToDefault'),
        editAuthorizedValues: t('edit'),
        saveChanges: t('save'),
        cancel: t('cancel'),
        confirmReset: t('metricsTable.confirmReset'),
        determiningLandArea: t('metrics.determiningSurfaceArea'),
        noneOption: t('metricsTable.noneOption'),
        headers: {
          indices: t('metricsTable.headers.indices'),
          current: t('metrics.current'),
          authorized: t('metrics.authorised'),
          difference: t('metrics.difference'),
        },
        groups: {
          heights: t('metricsTable.groups.heights'),
          floors: t('metricsTable.groups.floors'),
          setbacks: t('metricsTable.groups.setbacks'),
          usageRatios: t('metricsTable.groups.usageRatios'),
          environmental: t('metricsTable.groups.environmental'),
          other: t('metricsTable.groups.other'),
        },
        derivedSurfaces: {
          determiningBuiltArea: t(
            'metricsTable.derivedSurfaces.determiningBuiltArea'
          ),
          determiningBuiltAreaDesc: t(
            'metricsTable.derivedSurfaces.determiningBuiltAreaDesc'
          ),
          builtArea: t('metricsTable.derivedSurfaces.builtArea'),
          builtAreaDesc: t('metricsTable.derivedSurfaces.builtAreaDesc'),
          determiningFloorArea: t(
            'metricsTable.derivedSurfaces.determiningFloorArea'
          ),
          determiningFloorAreaDesc: t(
            'metricsTable.derivedSurfaces.determiningFloorAreaDesc'
          ),
          grossUsefulFloorArea: t(
            'metricsTable.derivedSurfaces.grossUsefulFloorArea'
          ),
          grossUsefulFloorAreaDesc: t(
            'metricsTable.derivedSurfaces.grossUsefulFloorAreaDesc'
          ),
          floorArea: t('metricsTable.derivedSurfaces.floorArea'),
          floorAreaDesc: t('metricsTable.derivedSurfaces.floorAreaDesc'),
          builtVolume: t('metricsTable.derivedSurfaces.builtVolume'),
          builtVolumeDesc: t('metricsTable.derivedSurfaces.builtVolumeDesc'),
        },
        metricLabels: {
          cos: t('metricLabels.cos'),
          cus: t('metricLabels.cus'),
          ibus: t('metricLabels.ibus'),
          ios: t('metricLabels.ios'),
          ius: t('metricLabels.ius'),
          im: t('metricLabels.im'),
          vbr: t('metricLabels.vbr'),
          altitude: t('metricLabels.altitude'),
          roofHeight: t('metricLabels.roofHeight'),
          totalHeight: t('metricLabels.totalHeight'),
          heightWithoutRoof: t('metricLabels.heightWithoutRoof'),
          totalFloors: t('metricLabels.totalFloors'),
          mainFloors: t('metricLabels.mainFloors'),
          atticFloors: t('metricLabels.atticFloors'),
          undergroundFloors: t('metricLabels.undergroundFloors'),
          commercialRatio: t('metricLabels.commercialRatio'),
          livingRatio: t('metricLabels.livingRatio'),
          officeRatio: t('metricLabels.officeRatio'),
          greenRatio: t('metricLabels.greenRatio'),
          noiseSensitivityLevel: t('metricLabels.noiseSensitivityLevel'),
          bigSetback: t('metricLabels.bigSetback'),
          smallSetback: t('metricLabels.smallSetback'),
          buildingDepth: t('metricLabels.buildingDepth'),
          density: t('metricLabels.density'),
          openSpaceRatio: t('metricLabels.openSpaceRatio'),
          roofSlope: t('metricLabels.roofSlope'),
          distanceLimit: t('metricLabels.distanceLimit'),
          numberOfUnits: t('metricLabels.numberOfUnits'),
          constructionArrangement: t('metricLabels.constructionArrangement'),
          maxLength: t('metricLabels.maxLength'),
        },
        progressBar: {
          currentSituation: t('progressBar.currentSituation'),
          used: t('progressBar.used'),
          authorized: t('progressBar.authorized'),
          utilization: t('progressBar.utilization'),
          remainingPotential: t('progressBar.remainingPotential'),
          available: t('progressBar.available'),
        },
        concepts: {
          occupation: t('concepts.occupation'),
          utilization: t('concepts.utilization'),
        },
        visibility: {
          hide: t('visibility.hide'),
          show: t('visibility.show'),
          addMetric: t('visibility.addMetric'),
        },
        envelope: {
          anchorLabel: t('envelope.anchorLabel'),
          anchorSub: t('envelope.anchorSub'),
          authorisedCell: t('envelope.authorisedCell'),
          buildableFootprint: t('envelope.buildableFootprint'),
          maxBuildingVolume: t('envelope.maxBuildingVolume'),
          caveatMinSetback: t('envelope.caveatMinSetback'),
          caveatRoofAllowance: t('envelope.caveatRoofAllowance'),
          capNote: t('envelope.capNote'),
          contiguousChip: t('envelope.contiguousChip'),
          unbuildableBanner: t('envelope.unbuildableBanner'),
          lockedNote: t('envelope.lockedNote'),
          recomputedFromHeight: t('envelope.recomputedFromHeight'),
          previewFromSetback: t('envelope.previewFromSetback'),
          inputTag: t('envelope.inputTag'),
          referenceOnlySub: t('envelope.referenceOnlySub'),
          manualReplacesSub: t('envelope.manualReplacesSub'),
          s7Note: t('envelope.s7Note'),
          calculatedValueLabel: t('envelope.calculatedValueLabel'),
          calcTitle: t('envelope.calcTitle'),
          disclaimer: t('envelope.disclaimer'),
          calc: {
            constructibleSurface: t('envelope.calc.constructibleSurface'),
            constructibleSurfaceSub: t('envelope.calc.constructibleSurfaceSub'),
            smallSetback: t('envelope.calc.smallSetback'),
            smallSetbackSub: t('envelope.calc.smallSetbackSub'),
            smallSetbackContiguousSub: t(
              'envelope.calc.smallSetbackContiguousSub'
            ),
            contiguousLabel: t('envelope.calc.contiguousLabel'),
            contiguousSub: t('envelope.calc.contiguousSub'),
            largeSetback: t('envelope.calc.largeSetback'),
            largeSetbackUnknownSub: t('envelope.calc.largeSetbackUnknownSub'),
            unknownValue: t('envelope.calc.unknownValue'),
            buildableFootprint: t('envelope.calc.buildableFootprint'),
            capsLabel: t('envelope.calc.capsLabel'),
            capsNotApplied: t('envelope.calc.capsNotApplied'),
            capsAppliedSub: t('envelope.calc.capsAppliedSub'),
            effectiveFootprint: t('envelope.calc.effectiveFootprint'),
            totalHeight: t('envelope.calc.totalHeight'),
            facadeHeight: t('envelope.calc.facadeHeight'),
            roofAllowance: t('envelope.calc.roofAllowance'),
            roofAllowanceSub: t('envelope.calc.roofAllowanceSub'),
            maxVolume: t('envelope.calc.maxVolume'),
            existingVolume: t('envelope.calc.existingVolume'),
            existingVolumeSub: t('envelope.calc.existingVolumeSub'),
            remainingPotential: t('envelope.calc.remainingPotential'),
            aboveEnvelope: t('envelope.calc.aboveEnvelope'),
          },
        },
        unitLabels: {
          floors: t('metricsTable.unitLabels.floors'),
          units: t('metricsTable.unitLabels.units'),
        },
      },
      sourceLabels: {
        manual: t('sourceLabels.manual'),
        geoportal: t('sourceLabels.geoportal'),
        llm: t('sourceLabels.llm'),
        estimate: t('sourceLabels.estimate'),
        old: t('sourceLabels.old'),
      },
      status: {
        current: t('status.current'),
        future: t('status.future'),
        past: t('status.past'),
      },
      header: {
        unknownZone: t('unknownZone'),
        buildingArea: t('header.buildingArea'),
        nonBuildingArea: t('header.nonBuildingArea'),
        adopted: t('header.adopted'),
        abrogated: t('header.abrogated'),
        purpose: t('purpose'),
        constructionArrangement: t('header.constructionArrangement'),
        constructionArrangementValues: {
          C: t('header.constructionArrangementValues.C'),
          NC: t('header.constructionArrangementValues.NC'),
          'C / NC': t('header.constructionArrangementValues.C / NC'),
        },
        references: t('header.references'),
        documents: t('header.documents'),
        segments: {
          potential: t('segments.potential'),
          purposeRules: t('segments.purposeRules'),
          documents: t('header.documents'),
        },
        askQuestion: t('zoneQA.title'),
        plotCoverage: t('header.plotCoverage'),
      },
      documents: {
        title: t('documents.title'),
        loading: t('documents.loading'),
        noDocuments: t('documents.noDocuments'),
        view: t('documents.view'),
        modalTitle: t('documents.modalTitle'),
        searchPlaceholder: t('documents.searchPlaceholder'),
        searchAriaLabel: t('documents.searchAriaLabel'),
        clearSearchAriaLabel: t('documents.clearSearchAriaLabel'),
        noDocumentSelected: t('documents.noDocumentSelected'),
        selectDocumentHint: t('documents.selectDocumentHint'),
        important: t('documents.important'),
        noPreviewAvailable: t('documents.noPreviewAvailable'),
        noPreviewHint: t('documents.noPreviewHint'),
        documentTypes: {
          rule: t('documents.documentTypes.rule'),
          map: t('documents.documentTypes.map'),
        },
        sourceLabels: {
          lupa: t('documents.sourceLabels.lupa'),
          lupc: t('documents.sourceLabels.lupc'),
          lupo: t('documents.sourceLabels.lupo'),
          lup: t('documents.sourceLabels.lup'),
          region: t('documents.sourceLabels.region'),
          country: t('documents.sourceLabels.country'),
        },
        sourceTitles: {
          lupa: t('documents.sourceTitles.lupa'),
          lupc: t('documents.sourceTitles.lupc'),
          lupo: t('documents.sourceTitles.lupo'),
          lup: t('documents.sourceTitles.lup'),
          region: t('documents.sourceTitles.region'),
          country: t('documents.sourceTitles.country'),
        },
      },
      referenceViewer: {
        title: t('referenceViewer.title'),
        searchPlaceholder: t('referenceViewer.searchPlaceholder'),
        searchAriaLabel: t('referenceViewer.searchAriaLabel'),
        clearSearchAriaLabel: t('referenceViewer.clearSearchAriaLabel'),
        noReferenceSelected: t('referenceViewer.noReferenceSelected'),
        selectReferenceHint: t('referenceViewer.selectReferenceHint'),
        noPreviewAvailable: t('referenceViewer.noPreviewAvailable'),
        noPreviewHint: t('referenceViewer.noPreviewHint'),
        noReferences: t('referenceViewer.noReferences'),
        pageLabel: t('referenceViewer.pageLabel'),
        pagesLabel: t('referenceViewer.pagesLabel'),
      },
      legend: {
        title: t('legend.title'),
        sources: {
          title: t('legend.sources.title'),
          geoportal: t('legend.sources.geoportal'),
          regulation: t('legend.sources.regulation'),
          algorithm: t('legend.sources.algorithm'),
          manual: t('legend.sources.manual'),
          userEdit: t('legend.sources.userEdit'),
        },
        agreement: {
          title: t('legend.agreement.title'),
          identical: t('legend.agreement.identical'),
          conflict: t('legend.agreement.conflict'),
          unique: t('legend.agreement.unique'),
          conflictNote: t('legend.agreement.conflictNote'),
        },
        difference: {
          title: t('legend.difference.title'),
          remaining: t('legend.difference.remaining'),
          exceeded: t('legend.difference.exceeded'),
        },
        authorizedValues: {
          title: t('legend.authorizedValues.title'),
          clickForDetails: t('legend.authorizedValues.clickForDetails'),
          notAvailable: t('legend.authorizedValues.notAvailable'),
        },
      },
    }),
    [t, readOnly, entity?.address_full]
  )

  // Push effective distance limit to map section data when it changes
  const handleDistanceLimitChange = useCallback(
    (value: number | undefined) => {
      if (typeof value === 'number' && value > 0) {
        updateMapData({ distanceLimitValue: value })
      }
    },
    [updateMapData]
  )

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px', color: '#999' }}>
        {t('messages.loading')}
      </div>
    )
  }

  if (error || !entity) {
    return (
      <div style={{ textAlign: 'center', padding: '48px', color: '#ff3b30' }}>
        {error || t('messages.error')}
      </div>
    )
  }

  // ReadOnly: check if zone data exists (old shares may not have it)
  if (readOnly) {
    const hasZoneData =
      zoningData?.zones &&
      (zoningData.zones.lupa?.length > 0 ||
        zoningData.zones.lupc?.length > 0 ||
        zoningData.zones.lupo?.length > 0 ||
        zoningData.zones.lup?.length > 0)

    if (!hasZoneData) {
      return (
        <div style={{ textAlign: 'center', padding: '48px', color: '#999' }}>
          {t('messages.noData')}
        </div>
      )
    }
  }

  return (
    <ZoningSection
      land={land as any}
      underExploitedScore={underExploitedScore}
      getZonesByIds={getZonesByIds}
      landData={landData}
      landId={landId}
      zoningOverridesService={zoningOverridesService}
      zoningService={zoningService}
      onDataChange={readOnly ? undefined : handleZoningDataChange}
      translations={translations}
      locale={i18n.language}
      readOnly={readOnly}
      zoneQAService={zoneQAService}
      zoneQATranslations={zoneQATranslations}
      onTranscribeVoice={onTranscribeVoice}
      onReportIncorrect={onReportIncorrect}
      onFetchEstimateStats={onFetchEstimateStats}
      onDistanceLimitChange={handleDistanceLimitChange}
    />
  )
}
